import { useId, type ReactNode } from 'react';
import s from './Field.module.css';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Classe de mise en page appliquée à l'enveloppe (largeur, flex, grid-area). */
  className?: string;
  children: (ids: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

/** Enveloppe libellé + aide + erreur ; fournit les ids ARIA au champ enfant. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={className ? `${s.field} ${className}` : s.field}>
      {label && (
        <label htmlFor={id} className={s.label}>
          {label}
          {required && <span className={s.req} aria-hidden="true"> *</span>}
        </label>
      )}
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint && !error && (
        <p id={hintId} className={s.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={s.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
